// WebSocket Message Type Definitions
// This file defines all WebSocket message schemas that flow through the WebSocket service
import type { ActorSystemMap } from '~/features/xstateSystem';

/**
 * Base WebSocket message envelope from server
 * All messages follow this structure with type-based routing
 */
export interface WSMessageEnvelope {
  type: MessageType;
  data: unknown;
  timestamp: string;
  id?: string;
}

/**
 * All supported WebSocket message types
 * Add new types here as the system grows
 */
export type MessageType =
  | 'chatbot' // AI chatbot messages
  | 'notification' // Push notifications
  | 'notification.user' // User-specific notifications
  | 'provider-association.user' // impersonation users' flow notifications
  | 'ai-job.user'; // AI document parsing job updates

// ============================================================================
// CHATBOT MESSAGES
// ============================================================================

export interface WSChatbotMessage {
  type: 'chatbot';
  data: WSChatbotData;
  timestamp: string;
  id?: string;
}

export interface WSChatbotData {
  text: string;
  conversationId: string;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// NOTIFICATION MESSAGES
// ============================================================================

export interface WSNotificationMessage {
  type: 'notification' | 'notification.user';
  data: WSNotificationData;
  timestamp: string;
  id?: string;
}

export interface WSNotificationData {
  category: NotificationCategory;
  notification: NotificationPayload;
  callback?: WSMessageCallback;
}

/**
 * Notification categories determine how messages are processed
 * - p2p: Direct peer-to-peer messages between users
 * - process: Server process updates (success, warning, failure, info)
 */
export type NotificationCategory = 'p2p' | 'process';

/**
 * Notification severity levels
 */
export type NotificationSeverity = 'success' | 'warning' | 'failure' | 'info';

/**
 * Notification persistence behavior
 * - persisted: Stored in database, updated via API calls, survives session
 * - ephemeral: Temporary, dismissed after interaction, not stored
 */
export type NotificationPersistence = 'persisted' | 'ephemeral';

/**
 * Core notification payload structure
 */
export interface NotificationPayload {
  id: string;
  title: string;
  body: string;
  severity: NotificationSeverity;
  persistence: NotificationPersistence;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
  /** Server-side `extra_data`; vault change notifications name their section in `section.url`. */
  extraData?: { section?: { url?: unknown } } & Record<string, unknown>;
  createdAt?: string;
}

// ============================================================================
// AI PARSE JOB MESSAGES
// ============================================================================

export interface WSAIParseJobMessage {
  type: 'aiParseJob';
  data: WSAIParseJobData;
  timestamp: string;
  id?: string;
}

export interface WSAIParseJobData {
  jobId: string | number;
  documentId: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  error?: string;
  result?: unknown;
}

// ============================================================================
// AI JOB UPDATE MESSAGES (NEW)
// ============================================================================

export interface WSAiJobUpdateMessage {
  type: 'ai-job.user';
  data: WSAiJobUpdateData;
  timestamp: string;
  id?: string;
}

export type AiJobType = 'cv-parse' | 'packet-parse' | 'insurance-parse' | 'document-parse';

export interface WSAiJobUpdateData {
  jobId: string | number;
  documentId: number;
  jobType: AiJobType;
  status: 'PENDING' | 'STARTED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress?: number; // 0-100
  stage?: string; // e.g., "OCR extraction", "Parsing", "Saving"
  error?: string;
  resultId?: number; // AiJob ID when complete
}

export type AiJobStatus = WSAiJobUpdateData['status'];

export const isInsuranceParseCompleted = (data: WSAiJobUpdateData): boolean =>
  data.jobType === 'insurance-parse' && data.status === 'COMPLETED';

// Helper for status display
export const AiJobStatusDisplay: Record<
  AiJobStatus,
  { label: string; variant: 'default' | 'processing' | 'success' | 'error' }
> = {
  PENDING: { label: 'Queued', variant: 'default' },
  STARTED: { label: 'Starting', variant: 'processing' },
  PROCESSING: { label: 'Processing', variant: 'processing' },
  COMPLETED: { label: 'Complete', variant: 'success' },
  FAILED: { label: 'Failed', variant: 'error' },
};

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if a message is a chatbot message
 */
export function isChatbotMessage(msg: WSMessageEnvelope): msg is WSChatbotMessage {
  return msg.type === 'chatbot';
}

/**
 * Type guard to check if a message is a notification message.
 *
 * Also validates the payload shape (`data.notification` present and object-shaped)
 * so downstream destructures like `const { notification } = message.data` are
 * safe. Servers can send `type: 'notification'` with a missing/null `data`
 * (e.g. malformed upload-service callbacks) — narrowing purely on `.type`
 * would let those through and crash the app at the destructure site.
 */
export function isNotificationMessage(msg: WSMessageEnvelope): msg is WSNotificationMessage {
  if (msg.type !== 'notification' && msg.type !== 'notification.user') return false;
  const data = msg.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    'notification' in data &&
    typeof (data as { notification: unknown }).notification === 'object' &&
    (data as { notification: unknown }).notification !== null
  );
}

/**
 * Type guard to check if a message is an AI job update message (new format)
 */
export function isAiJobUpdateMessage(msg: WSMessageEnvelope): msg is WSAiJobUpdateMessage {
  return msg.type === 'ai-job.user';
}

/**
 * Parse a raw WebSocket message and validate its structure
 * Returns null if the message is invalid
 */
export function parseWSMessage(raw: string): WSMessageEnvelope | null {
  try {
    const parsed = JSON.parse(raw);
    // `parsed.data != null` rejects both `undefined` AND `null` — previously
    // only `undefined` was filtered, so `data: null` envelopes slipped through
    // and crashed downstream destructures.
    if (parsed?.type && parsed.data != null) {
      return parsed as WSMessageEnvelope;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Check if a notification is ephemeral (should disappear after interaction)
 */
export function isEphemeralNotification(persistence: NotificationPersistence): boolean {
  return persistence === 'ephemeral';
}

/**
 * [MCV-1800] The title of the notification the API sends when an upload finishes encrypting
 * (UPLOAD_COMPLETE in apps/mcv-api/mcv_api/routes/document/constants.py). Only a document that
 * is stored without parsing triggers it. The Documents page already summarises every upload it
 * starts, so the client does not raise a second "complete" toast for it.
 */
export const DOCUMENT_UPLOAD_COMPLETE_TITLE = 'Document Upload Complete';

/**
 * Check if a notification is persisted (stored in database)
 */
export function isPersistedNotification(persistence: NotificationPersistence): boolean {
  return persistence === 'persisted';
}

// ============================================================================
// PROVIDER ASSOCIATION UPDATE MESSAGES (NEW)
// ============================================================================

export type ProviderAssociationEventType =
  | 'INVITATION_SENT'
  | 'INVITATION_RECEIVED'
  | 'INVITATION_ACCEPTED'
  | 'INVITATION_DECLINED'
  | 'INVITATION_REVOKED'
  | 'ASSOCIATION_CREATED'
  | 'ASSOCIATION_REMOVED';

export interface WSProviderAssociationMessage {
  type: 'provider-association.user';
  data: WSProviderAssociationData;
  timestamp: string;
  id?: string;
}

export interface WSProviderAssociationData {
  associationId: number | null;
  invitationId: number | null;
  employerId: number | null;
  notificationId: number | null;
  providerId: number | null;
  eventType: ProviderAssociationEventType;
  link?: string;
}

// ============================================================================
// GENERIC CALLBACK SYSTEM (NEW)
// ============================================================================

/**
 * Generic callback configuration for websocket messages
 * Allows any message type to trigger UI updates, endpoint calls, or navigation
 */
export interface WSMessageCallback {
  /** Machine to send event to (if routed to XState machine) */
  targetMachine?: keyof ActorSystemMap;

  /** Event type to send to target machine */
  eventType?: string;

  /** Event payload to send */
  payload?: Record<string, unknown>;

  /** Custom event name (if routed via CustomEvent) */
  customEventName?: string;

  /** Show alert notification */
  showAlert?: boolean;
  alertConfig?: {
    text: string;
    subText?: string;
    type: 'info' | 'success' | 'warning' | 'error';
    timeout?: number;
  };

  /** Navigate to URL */
  navigateTo?: string;

  /** Trigger data refetch for specific service */
  refetchService?: keyof ActorSystemMap;
  refetchEvent?: string;
}

/**
 * Enhanced message metadata with callback configuration
 */
export interface WSMessageMeta<T = unknown> {
  type: MessageType;
  data: T;
  timestamp: string;
  id?: string;
  callback?: WSMessageCallback;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isProviderAssociationMessage(
  msg: WSMessageEnvelope
): msg is WSProviderAssociationMessage {
  return msg.type === 'provider-association.user';
}

export function hasCallbackConfig(
  msg: WSMessageEnvelope
): msg is WSMessageEnvelope & { callback: WSMessageCallback } {
  return 'callback' in msg && !!msg.callback;
}
